import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Palette, 
    Coins, 
    Building2,
    Mail,
    Save,
    Loader2,
    Send,
    CheckCircle2,
    Upload,
    Trash2,
    ImageIcon,
    BookOpen,
    FileText,
    UserPlus,
    ScrollText,
    Globe,
    QrCode,
    Copy,
    Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettingsPage = () => {
    const { token } = useAuth();
    const { tenant, updateTenant } = useTenant();
    const fileInputRef = useRef(null);
    const pdfInputRef = useRef(null);
    
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [testingEmail, setTestingEmail] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [deletingPdf, setDeletingPdf] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [savingInvitation, setSavingInvitation] = useState(false);
    const [savingTerms, setSavingTerms] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        logo: '',
        primary_color: '#0f172a',
        points_per_conversion: 250,
        welcome_message: '',
        invitation_email_text: '',
        invitation_pdf_url: '',
        terms_and_conditions: '',
        custom_domain: ''
    });
    
    const [emailConfig, setEmailConfig] = useState({
        email_type: 'msgraph',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_use_tls: true,
        api_endpoint: '',
        api_key: '',
        api_auth_type: 'bearer',
        api_auth_header: 'Authorization',
        from_email: '',
        from_name: '',
        ms_tenant_id: '',
        ms_client_id: '',
        ms_client_secret: ''
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/admin/settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings(response.data);
            setFormData({
                name: response.data.name || '',
                logo: response.data.logo || '',
                primary_color: response.data.primary_color || '#0f172a',
                points_per_conversion: response.data.points_per_conversion || 250,
                welcome_message: response.data.welcome_message || '',
                invitation_email_text: response.data.invitation_email_text || '',
                invitation_pdf_url: response.data.invitation_pdf_url || '',
                terms_and_conditions: response.data.terms_and_conditions || '',
                custom_domain: response.data.custom_domain || ''
            });
            
            if (response.data.email_config) {
                setEmailConfig(prev => ({
                    ...prev,
                    ...response.data.email_config
                }));
            }
        } catch (error) {
            console.error('Failed to fetch settings:', error);
            toast.error('Einstellungen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (file) => {
        if (!file) return;
        
        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Nicht unterstütztes Dateiformat. Erlaubt: PNG, JPG, SVG, WebP, GIF');
            return;
        }
        
        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Datei ist zu groß. Maximale Größe: 2MB');
            return;
        }
        
        setUploadingLogo(true);
        
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            const response = await axios.post(
                `${API}/admin/upload-logo`,
                formDataUpload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            
            const logoUrl = response.data.logo_url;
            setFormData(prev => ({ ...prev, logo: logoUrl }));
            updateTenant({ logo: logoUrl });
            toast.success('Logo erfolgreich hochgeladen');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Logo-Upload fehlgeschlagen');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleDeleteLogo = async () => {
        setUploadingLogo(true);
        try {
            await axios.delete(`${API}/admin/logo`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setFormData(prev => ({ ...prev, logo: '' }));
            updateTenant({ logo: null });
            toast.success('Logo gelöscht');
        } catch (error) {
            toast.error('Logo konnte nicht gelöscht werden');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handlePdfUpload = async (file) => {
        if (!file) return;
        
        // Validate file type
        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Nur PDF-Dateien sind erlaubt');
            return;
        }
        
        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Datei ist zu groß. Maximale Größe: 10MB');
            return;
        }
        
        setUploadingPdf(true);
        
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            const response = await axios.post(
                `${API}/admin/settings/invitation-pdf`,
                formDataUpload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            
            const pdfUrl = response.data.invitation_pdf_url;
            setFormData(prev => ({ ...prev, invitation_pdf_url: pdfUrl }));
            toast.success('PDF erfolgreich hochgeladen');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'PDF-Upload fehlgeschlagen');
        } finally {
            setUploadingPdf(false);
        }
    };

    const handleDeletePdf = async () => {
        setDeletingPdf(true);
        try {
            await axios.delete(`${API}/admin/settings/invitation-pdf`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setFormData(prev => ({ ...prev, invitation_pdf_url: '' }));
            toast.success('PDF gelöscht');
        } catch (error) {
            toast.error('PDF konnte nicht gelöscht werden');
        } finally {
            setDeletingPdf(false);
        }
    };

    const handlePdfFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            handlePdfUpload(e.target.files[0]);
        }
    };

    const handleSaveInvitationSettings = async () => {
        setSavingInvitation(true);
        try {
            await axios.put(
                `${API}/admin/settings`,
                {
                    invitation_email_text: formData.invitation_email_text || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Einladungs-Einstellungen gespeichert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSavingInvitation(false);
        }
    };

    const handleSaveTermsSettings = async () => {
        setSavingTerms(true);
        try {
            await axios.put(
                `${API}/admin/settings`,
                {
                    terms_and_conditions: formData.terms_and_conditions || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Teilnahmebedingungen gespeichert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSavingTerms(false);
        }
    };

    const getPdfUrl = (pdfPath) => {
        if (!pdfPath) return null;
        if (pdfPath.startsWith('http')) return pdfPath;
        return `${process.env.REACT_APP_BACKEND_URL}${pdfPath}`;
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleLogoUpload(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleLogoUpload(e.target.files[0]);
        }
    };

    const getLogoUrl = (logo) => {
        if (!logo) return null;
        if (logo.startsWith('http')) return logo;
        return `${process.env.REACT_APP_BACKEND_URL}${logo}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await axios.put(
                `${API}/admin/settings`,
                {
                    name: formData.name,
                    logo: formData.logo || null,
                    primary_color: formData.primary_color,
                    points_per_conversion: parseInt(formData.points_per_conversion),
                    welcome_message: formData.welcome_message || null,
                    custom_domain: formData.custom_domain || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            updateTenant({
                name: formData.name,
                logo: formData.logo,
                primary_color: formData.primary_color,
                welcome_message: formData.welcome_message,
                custom_domain: formData.custom_domain
            });
            
            toast.success('Einstellungen gespeichert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setSavingEmail(true);

        try {
            await axios.put(
                `${API}/admin/settings/email`,
                emailConfig,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('E-Mail-Einstellungen gespeichert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSavingEmail(false);
        }
    };

    const handleTestEmail = async () => {
        if (!testEmail) {
            toast.error('Bitte geben Sie eine Test-E-Mail-Adresse ein');
            return;
        }
        
        setTestingEmail(true);
        try {
            await axios.post(
                `${API}/admin/settings/email/test`,
                { to_email: testEmail },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Test-E-Mail wurde gesendet');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Test-E-Mail fehlgeschlagen');
        } finally {
            setTestingEmail(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                        <Card key={i} className="animate-pulse">
                            <CardContent className="p-6">
                                <div className="h-8 bg-slate-200 rounded w-1/4 mb-4" />
                                <div className="h-10 bg-slate-200 rounded w-full" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Einstellungen</h1>
                    <p className="text-slate-600 mt-1">
                        Verwalten Sie Ihre Organisationseinstellungen und Branding
                    </p>
                </div>

                <Tabs defaultValue="general" className="space-y-6">
                    <TabsList>
                        <TabsTrigger value="general" data-testid="tab-general">Allgemein</TabsTrigger>
                        <TabsTrigger value="invitation" data-testid="tab-invitation">Einladung</TabsTrigger>
                        <TabsTrigger value="terms" data-testid="tab-terms">Teilnahmebedingungen</TabsTrigger>
                        <TabsTrigger value="email" data-testid="tab-email">E-Mail</TabsTrigger>
                    </TabsList>

                    {/* General Settings Tab */}
                    <TabsContent value="general">
                        <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
                            {/* Branding */}
                            <Card data-testid="branding-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <Building2 size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Organisation</CardTitle>
                                            <CardDescription>Grundlegende Organisationsinformationen</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name">Organisationsname</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Ihr Firmenname"
                                            data-testid="org-name-input"
                                        />
                                    </div>
                                    
                                    {/* Logo Upload */}
                                    <div className="space-y-2">
                                        <Label>Logo</Label>
                                        <div 
                                            className={`border-2 border-dashed rounded-xl p-6 transition-colors ${
                                                dragActive 
                                                    ? 'border-blue-500 bg-blue-50' 
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            onDragEnter={handleDrag}
                                            onDragLeave={handleDrag}
                                            onDragOver={handleDrag}
                                            onDrop={handleDrop}
                                            data-testid="logo-upload-area"
                                        >
                                            {formData.logo ? (
                                                <div className="flex items-center gap-4">
                                                    <div className="flex-shrink-0 w-32 h-16 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                                                        <img 
                                                            src={getLogoUrl(formData.logo)} 
                                                            alt="Logo" 
                                                            className="max-w-full max-h-full object-contain"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm text-slate-600 mb-2">Aktuelles Logo</p>
                                                        <div className="flex gap-2">
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                disabled={uploadingLogo}
                                                            >
                                                                <Upload size={14} className="mr-1" />
                                                                Ändern
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                onClick={handleDeleteLogo}
                                                                disabled={uploadingLogo}
                                                                data-testid="delete-logo-btn"
                                                            >
                                                                <Trash2 size={14} className="mr-1" />
                                                                Entfernen
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div 
                                                    className="text-center cursor-pointer"
                                                    onClick={() => fileInputRef.current?.click()}
                                                >
                                                    {uploadingLogo ? (
                                                        <Loader2 size={40} className="mx-auto text-slate-400 animate-spin mb-3" />
                                                    ) : (
                                                        <ImageIcon size={40} className="mx-auto text-slate-300 mb-3" />
                                                    )}
                                                    <p className="text-sm text-slate-600 mb-1">
                                                        {uploadingLogo ? 'Wird hochgeladen...' : 'Logo hier ablegen oder klicken'}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        PNG, JPG, SVG, WebP • Max. 2MB • Empfohlen: 200x60px
                                                    </p>
                                                </div>
                                            )}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp,image/gif"
                                                className="hidden"
                                                onChange={handleFileSelect}
                                                data-testid="logo-file-input"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Theme */}
                            <Card data-testid="theme-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <Palette size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Branding</CardTitle>
                                            <CardDescription>Passen Sie Ihre Markenfarben an</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="primary_color">Primärfarbe</Label>
                                        <div className="flex gap-3">
                                            <div className="relative">
                                                <input
                                                    type="color"
                                                    id="primary_color"
                                                    value={formData.primary_color}
                                                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                                    className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer"
                                                    data-testid="primary-color-picker"
                                                />
                                            </div>
                                            <Input
                                                value={formData.primary_color}
                                                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                                                placeholder="#0f172a"
                                                className="flex-1"
                                                data-testid="primary-color-input"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Preview */}
                                    <div className="pt-4 border-t border-slate-100">
                                        <p className="text-sm text-slate-500 mb-3">Vorschau</p>
                                        <div className="flex items-center gap-4">
                                            <Button type="button" style={{ backgroundColor: formData.primary_color }}>
                                                Primärer Button
                                            </Button>
                                            <div 
                                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-medium"
                                                style={{ backgroundColor: formData.primary_color }}
                                            >
                                                A
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Points */}
                            <Card data-testid="points-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <Coins size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Punkte</CardTitle>
                                            <CardDescription>Konfigurieren Sie Punktwerte</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="points_per_conversion">Punkte pro Konvertierung</Label>
                                        <Input
                                            id="points_per_conversion"
                                            type="number"
                                            min="1"
                                            value={formData.points_per_conversion}
                                            onChange={(e) => setFormData({ ...formData, points_per_conversion: e.target.value })}
                                            data-testid="points-per-conversion-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Punkte, die dem Empfehlungsgeber gutgeschrieben werden, wenn seine Empfehlung konvertiert wird
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Custom Domain */}
                            <Card data-testid="custom-domain-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <Globe size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Custom Domain</CardTitle>
                                            <CardDescription>Eigene Domain für Einladungslinks</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="custom_domain">Domain für Einladungen und QR-Codes</Label>
                                        <Input
                                            id="custom_domain"
                                            value={formData.custom_domain}
                                            onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                                            placeholder="empfehlung.ihre-firma.de"
                                            data-testid="custom-domain-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Wenn gesetzt, werden alle Einladungslinks und QR-Codes diese Domain verwenden.
                                            Lassen Sie das Feld leer, um die Standard-Domain zu verwenden.
                                        </p>
                                    </div>
                                    {formData.custom_domain && (
                                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                            <p className="text-sm text-blue-800">
                                                <strong>Beispiel-Link:</strong><br />
                                                https://{formData.custom_domain}/join?c=abc123
                                            </p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Direct Lead QR Code */}
                            <Card data-testid="direct-lead-qr-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <QrCode size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Direkt-Lead QR-Code</CardTitle>
                                            <CardDescription>Lead-Formular ohne Vermittler-Registrierung</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-slate-600">
                                        Mit diesem QR-Code können Interessenten direkt ihre Kontaktdaten hinterlassen, 
                                        ohne sich als Kunde oder Vermittler registrieren zu müssen.
                                    </p>
                                    
                                    <div className="p-4 bg-slate-50 rounded-lg border text-center">
                                        <img 
                                            src={`${API}/admin/direct-lead-qr?size=200`}
                                            alt="Direkt-Lead QR-Code"
                                            className="mx-auto mb-3"
                                            style={{ width: 200, height: 200 }}
                                            data-testid="direct-lead-qr-image"
                                        />
                                        <p className="text-xs text-slate-500 mb-3">
                                            {formData.custom_domain 
                                                ? `https://${formData.custom_domain}/o/${tenant?.organization_code}/lead`
                                                : `${window.location.origin}/o/${tenant?.organization_code}/lead`
                                            }
                                        </p>
                                        <div className="flex gap-2 justify-center">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    const url = formData.custom_domain 
                                                        ? `https://${formData.custom_domain}/o/${tenant?.organization_code}/lead`
                                                        : `${window.location.origin}/o/${tenant?.organization_code}/lead`;
                                                    navigator.clipboard.writeText(url);
                                                    toast.success('Link kopiert');
                                                }}
                                                data-testid="copy-direct-lead-link"
                                            >
                                                <Copy size={14} className="mr-1" />
                                                Link kopieren
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={async () => {
                                                    try {
                                                        const response = await axios.get(
                                                            `${API}/admin/direct-lead-qr?size=400`,
                                                            { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                                                        );
                                                        const url = window.URL.createObjectURL(response.data);
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.download = `direkt-lead-qr-${tenant?.organization_code}.png`;
                                                        link.click();
                                                        window.URL.revokeObjectURL(url);
                                                        toast.success('QR-Code heruntergeladen');
                                                    } catch (error) {
                                                        toast.error('Download fehlgeschlagen');
                                                    }
                                                }}
                                                data-testid="download-direct-lead-qr"
                                            >
                                                <Download size={14} className="mr-1" />
                                                QR herunterladen
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Welcome Message */}
                            <Card data-testid="welcome-message-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <BookOpen size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Willkommensnachricht</CardTitle>
                                            <CardDescription>Anleitung für neue Kunden</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="welcome_message">Nachricht für Kunden beim Login</Label>
                                        <Textarea
                                            id="welcome_message"
                                            value={formData.welcome_message}
                                            onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                                            placeholder="Herzlich willkommen auf unserem Empfehlungsportal!&#10;&#10;So funktioniert's:&#10;1. Klicken Sie auf 'Empfehlung eintragen'&#10;2. Füllen Sie die Kontaktdaten aus&#10;3. Für jede konvertierte Empfehlung erhalten Sie Punkte&#10;4. Tauschen Sie Ihre Punkte gegen Prämien ein"
                                            rows={8}
                                            className="resize-none"
                                            data-testid="welcome-message-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Diese Nachricht wird neuen Kunden beim ersten Login als Pop-Up angezeigt. 
                                            Verwenden Sie Absätze (Leerzeilen) für eine bessere Lesbarkeit.
                                        </p>
                                    </div>
                                    {formData.welcome_message && (
                                        <div className="pt-4 border-t border-slate-100">
                                            <p className="text-sm text-slate-500 mb-3">Vorschau</p>
                                            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 whitespace-pre-line">
                                                {formData.welcome_message}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Save Button */}
                            <div className="flex justify-end">
                                <Button
                                    type="submit"
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    disabled={saving}
                                    data-testid="save-settings-btn"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Speichern...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} className="mr-2" />
                                            Einstellungen speichern
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </TabsContent>

                    {/* Invitation Settings Tab */}
                    <TabsContent value="invitation">
                        <div className="space-y-6 max-w-3xl">
                            {/* Invitation Email Text */}
                            <Card data-testid="invitation-email-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <UserPlus size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Einladungs-E-Mail</CardTitle>
                                            <CardDescription>Personalisieren Sie die E-Mail für neue Kunden-Einladungen</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="invitation_email_text">E-Mail-Text</Label>
                                        <Textarea
                                            id="invitation_email_text"
                                            value={formData.invitation_email_text}
                                            onChange={(e) => setFormData({ ...formData, invitation_email_text: e.target.value })}
                                            placeholder="Sie wurden eingeladen, an unserem Empfehlungsprogramm teilzunehmen!&#10;&#10;Empfehlen Sie uns weiter und verdienen Sie Punkte für jede erfolgreiche Empfehlung. Diese Punkte können Sie gegen attraktive Prämien eintauschen."
                                            rows={6}
                                            className="resize-none"
                                            data-testid="invitation-email-text-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Dieser Text erscheint im Hauptteil der Einladungs-E-Mail. 
                                            Lassen Sie das Feld leer für den Standardtext.
                                        </p>
                                    </div>
                                    {formData.invitation_email_text && (
                                        <div className="pt-4 border-t border-slate-100">
                                            <p className="text-sm text-slate-500 mb-3">Vorschau</p>
                                            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 whitespace-pre-line">
                                                {formData.invitation_email_text}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* PDF Flyer Upload */}
                            <Card data-testid="invitation-pdf-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <FileText size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">PDF-Flyer</CardTitle>
                                            <CardDescription>Fügen Sie einen Werbe-Flyer als PDF-Anhang hinzu</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>PDF-Datei</Label>
                                        <div 
                                            className={`border-2 border-dashed rounded-xl p-6 transition-colors ${
                                                formData.invitation_pdf_url 
                                                    ? 'border-green-200 bg-green-50' 
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            data-testid="pdf-upload-area"
                                        >
                                            {formData.invitation_pdf_url ? (
                                                <div className="flex items-center gap-4">
                                                    <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                                        <FileText size={24} className="text-red-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-slate-700">PDF-Flyer hochgeladen</p>
                                                        <p className="text-xs text-slate-500">Wird an jede Einladungs-E-Mail angehängt</p>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => window.open(getPdfUrl(formData.invitation_pdf_url), '_blank')}
                                                            data-testid="view-pdf-btn"
                                                        >
                                                            Ansehen
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => pdfInputRef.current?.click()}
                                                            disabled={uploadingPdf}
                                                        >
                                                            <Upload size={14} className="mr-1" />
                                                            Ändern
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            onClick={handleDeletePdf}
                                                            disabled={deletingPdf}
                                                            data-testid="delete-pdf-btn"
                                                        >
                                                            {deletingPdf ? (
                                                                <Loader2 size={14} className="animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <Trash2 size={14} className="mr-1" />
                                                                    Entfernen
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div 
                                                    className="text-center cursor-pointer"
                                                    onClick={() => pdfInputRef.current?.click()}
                                                >
                                                    {uploadingPdf ? (
                                                        <Loader2 size={40} className="mx-auto text-slate-400 animate-spin mb-3" />
                                                    ) : (
                                                        <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                                                    )}
                                                    <p className="text-sm text-slate-600 mb-1">
                                                        {uploadingPdf ? 'Wird hochgeladen...' : 'PDF hier ablegen oder klicken'}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        PDF • Max. 10MB
                                                    </p>
                                                </div>
                                            )}
                                            <input
                                                ref={pdfInputRef}
                                                type="file"
                                                accept="application/pdf"
                                                className="hidden"
                                                onChange={handlePdfFileSelect}
                                                data-testid="pdf-file-input"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Save Button */}
                            <div className="flex justify-end">
                                <Button
                                    onClick={handleSaveInvitationSettings}
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    disabled={savingInvitation}
                                    data-testid="save-invitation-settings-btn"
                                >
                                    {savingInvitation ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Speichern...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} className="mr-2" />
                                            Einstellungen speichern
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Terms and Conditions Tab */}
                    <TabsContent value="terms">
                        <div className="space-y-6 max-w-3xl">
                            <Card data-testid="terms-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <ScrollText size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">Teilnahmebedingungen</CardTitle>
                                            <CardDescription>
                                                Bedingungen und Hinweise für Ihre Tippgeber
                                            </CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="terms_and_conditions">Teilnahmebedingungen Text</Label>
                                        <Textarea
                                            id="terms_and_conditions"
                                            value={formData.terms_and_conditions}
                                            onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
                                            placeholder="1. Teilnahmeberechtigung&#10;Am Empfehlungsprogramm können alle registrierten Kunden teilnehmen.&#10;&#10;2. Punkte&#10;Für jede erfolgreiche Empfehlung erhalten Sie 250 Punkte.&#10;&#10;3. Prämien&#10;Gesammelte Punkte können gegen Prämien eingetauscht werden."
                                            rows={15}
                                            className="resize-none font-mono text-sm"
                                            data-testid="terms-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Dieser Text wird für Tippgeber im Dashboard unter "Teilnahmebedingungen" angezeigt.
                                            Verwenden Sie Absätze (Leerzeilen) und Nummerierung für eine bessere Lesbarkeit.
                                        </p>
                                    </div>
                                    {formData.terms_and_conditions && (
                                        <div className="pt-4 border-t border-slate-100">
                                            <p className="text-sm text-slate-500 mb-3">Vorschau</p>
                                            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 whitespace-pre-line max-h-64 overflow-y-auto">
                                                {formData.terms_and_conditions}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Save Button */}
                            <div className="flex justify-end">
                                <Button
                                    onClick={handleSaveTermsSettings}
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    disabled={savingTerms}
                                    data-testid="save-terms-btn"
                                >
                                    {savingTerms ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Speichern...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} className="mr-2" />
                                            Teilnahmebedingungen speichern
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Email Settings Tab */}
                    <TabsContent value="email">
                        <form onSubmit={handleEmailSubmit} className="space-y-6 max-w-3xl">
                            <Card data-testid="email-config-card">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                        >
                                            <Mail size={20} style={{ color: tenant?.primary_color }} />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">E-Mail-Konfiguration</CardTitle>
                                            <CardDescription>
                                                Konfigurieren Sie den E-Mail-Versand für Kontoaktivierungen
                                            </CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Email Type Selection */}
                                    <div className="space-y-2">
                                        <Label>E-Mail-Dienst</Label>
                                        <Select 
                                            value={emailConfig.email_type} 
                                            onValueChange={(value) => setEmailConfig({ ...emailConfig, email_type: value })}
                                        >
                                            <SelectTrigger data-testid="email-type-select">
                                                <SelectValue placeholder="Wählen Sie einen E-Mail-Dienst" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="msgraph">Microsoft Graph (Office 365)</SelectItem>
                                                <SelectItem value="smtp">SMTP Server</SelectItem>
                                                <SelectItem value="api">API (REST)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Microsoft Graph Settings */}
                                    {emailConfig.email_type === 'msgraph' && (
                                        <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                                            <h4 className="font-medium text-slate-900">Microsoft Graph Einstellungen</h4>
                                            <p className="text-sm text-slate-600">
                                                Nutzen Sie Microsoft Graph API für den E-Mail-Versand über Office 365 / Exchange Online.
                                            </p>
                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="ms_tenant_id">Tenant ID (Verzeichnis-ID)</Label>
                                                    <Input
                                                        id="ms_tenant_id"
                                                        value={emailConfig.ms_tenant_id}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, ms_tenant_id: e.target.value })}
                                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                                        data-testid="ms-tenant-id-input"
                                                    />
                                                    <p className="text-xs text-slate-500">
                                                        Azure Portal → Azure Active Directory → Übersicht → Mandanten-ID
                                                    </p>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="ms_client_id">Client ID (Anwendungs-ID)</Label>
                                                    <Input
                                                        id="ms_client_id"
                                                        value={emailConfig.ms_client_id}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, ms_client_id: e.target.value })}
                                                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                                        data-testid="ms-client-id-input"
                                                    />
                                                    <p className="text-xs text-slate-500">
                                                        Azure Portal → App-Registrierungen → Ihre App → Anwendungs-ID
                                                    </p>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="ms_client_secret">Client Secret</Label>
                                                    <Input
                                                        id="ms_client_secret"
                                                        type="password"
                                                        value={emailConfig.ms_client_secret}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, ms_client_secret: e.target.value })}
                                                        placeholder="••••••••••••••••••••"
                                                        data-testid="ms-client-secret-input"
                                                    />
                                                    <p className="text-xs text-slate-500">
                                                        Azure Portal → App-Registrierungen → Ihre App → Zertifikate & Geheimnisse
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* SMTP Settings */}
                                    {emailConfig.email_type === 'smtp' && (
                                        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                                            <h4 className="font-medium text-slate-900">SMTP-Einstellungen</h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="smtp_host">SMTP Host</Label>
                                                    <Input
                                                        id="smtp_host"
                                                        value={emailConfig.smtp_host}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
                                                        placeholder="smtp.beispiel.de"
                                                        data-testid="smtp-host-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="smtp_port">Port</Label>
                                                    <Input
                                                        id="smtp_port"
                                                        type="number"
                                                        value={emailConfig.smtp_port}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: parseInt(e.target.value) })}
                                                        placeholder="587"
                                                        data-testid="smtp-port-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="smtp_username">Benutzername</Label>
                                                    <Input
                                                        id="smtp_username"
                                                        value={emailConfig.smtp_username}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_username: e.target.value })}
                                                        placeholder="benutzer@beispiel.de"
                                                        data-testid="smtp-username-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="smtp_password">Passwort</Label>
                                                    <Input
                                                        id="smtp_password"
                                                        type="password"
                                                        value={emailConfig.smtp_password}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
                                                        placeholder="••••••••"
                                                        data-testid="smtp-password-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="smtp_tls">TLS verwenden</Label>
                                                <Switch
                                                    id="smtp_tls"
                                                    checked={emailConfig.smtp_use_tls}
                                                    onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, smtp_use_tls: checked })}
                                                    data-testid="smtp-tls-switch"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* API Settings */}
                                    {emailConfig.email_type === 'api' && (
                                        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                                            <h4 className="font-medium text-slate-900">API-Einstellungen</h4>
                                            <div className="space-y-2">
                                                <Label htmlFor="api_endpoint">API-Endpunkt</Label>
                                                <Input
                                                    id="api_endpoint"
                                                    value={emailConfig.api_endpoint}
                                                    onChange={(e) => setEmailConfig({ ...emailConfig, api_endpoint: e.target.value })}
                                                    placeholder="https://api.ihr-dienst.de/send-email"
                                                    data-testid="api-endpoint-input"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>Authentifizierungstyp</Label>
                                                    <Select 
                                                        value={emailConfig.api_auth_type} 
                                                        onValueChange={(value) => setEmailConfig({ ...emailConfig, api_auth_type: value })}
                                                    >
                                                        <SelectTrigger data-testid="api-auth-type-select">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="bearer">Bearer Token</SelectItem>
                                                            <SelectItem value="header">Custom Header</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="api_auth_header">Header Name</Label>
                                                    <Input
                                                        id="api_auth_header"
                                                        value={emailConfig.api_auth_header}
                                                        onChange={(e) => setEmailConfig({ ...emailConfig, api_auth_header: e.target.value })}
                                                        placeholder="Authorization"
                                                        data-testid="api-auth-header-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="api_key">API-Schlüssel / Token</Label>
                                                <Input
                                                    id="api_key"
                                                    type="password"
                                                    value={emailConfig.api_key}
                                                    onChange={(e) => setEmailConfig({ ...emailConfig, api_key: e.target.value })}
                                                    placeholder="Ihr API-Schlüssel"
                                                    data-testid="api-key-input"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Common Settings */}
                                    <div className="space-y-4 pt-4 border-t border-slate-200">
                                        <h4 className="font-medium text-slate-900">Absender-Einstellungen</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="from_email">Absender E-Mail</Label>
                                                <Input
                                                    id="from_email"
                                                    type="email"
                                                    value={emailConfig.from_email}
                                                    onChange={(e) => setEmailConfig({ ...emailConfig, from_email: e.target.value })}
                                                    placeholder="noreply@ihre-firma.de"
                                                    data-testid="from-email-input"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="from_name">Absender Name</Label>
                                                <Input
                                                    id="from_name"
                                                    value={emailConfig.from_name}
                                                    onChange={(e) => setEmailConfig({ ...emailConfig, from_name: e.target.value })}
                                                    placeholder="Ihre Firma"
                                                    data-testid="from-name-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Test Email */}
                            <Card data-testid="test-email-card">
                                <CardHeader>
                                    <CardTitle className="text-lg">E-Mail testen</CardTitle>
                                    <CardDescription>
                                        Senden Sie eine Test-E-Mail, um die Konfiguration zu überprüfen
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex gap-3">
                                        <Input
                                            type="email"
                                            value={testEmail}
                                            onChange={(e) => setTestEmail(e.target.value)}
                                            placeholder="test@beispiel.de"
                                            className="flex-1"
                                            data-testid="test-email-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleTestEmail}
                                            disabled={testingEmail}
                                            data-testid="send-test-email-btn"
                                        >
                                            {testingEmail ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Send size={16} className="mr-2" />
                                                    Test senden
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Save Button */}
                            <div className="flex justify-end">
                                <Button
                                    type="submit"
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    disabled={savingEmail}
                                    data-testid="save-email-settings-btn"
                                >
                                    {savingEmail ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Speichern...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={18} className="mr-2" />
                                            E-Mail-Einstellungen speichern
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};
